import React from "react";
import MuiButton from "@mui/material/Button";
import { Box } from "@mui/material";
import { ButtonVariantType, CustomButtonProps } from "./types";
import { RestrictedComponent } from "../../components/RestrictedComponent";

const variants = {
  default: {
    backgroundColor: "var(--button-default)",
    transition: "filter 0.3s ease",
    color: "var(--button-default-foreground)",
    border: "var(--button-default-border)",
    "&:hover": {
      backgroundColor: "var(--button-default-hover)",
      color: "var(--button-default-foreground-hover)",
      border: "var(--button-default-border-hover)",
    },
    "&.Mui-disabled": {
      backgroundColor: "var(--button-default-disabled)",
      color: "var(--button-default-disabled-foreground)",
    },
  },
  defaultOutlined: {
    backgroundColor: "transparent",
    border: "1px solid white",
    color: "white",
    transition: "ease 0.2s",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.08)",
      color: "white",
      border: "1px solid white",
    },
    "&.Mui-disabled": {
      backgroundColor: "transparent",
      color: "rgba(255, 255, 255, 0.5)",
      border: "1px solid rgba(255, 255, 255, 0.5)",
    },
  },
  primary: {
    backgroundColor: "var(--button-primary)",
    transition: "filter 0.3s ease",
    color: "var(--button-primary-foreground)",
    border: "var(--button-primary-border)",
    "&:hover": {
      backgroundColor: "var(--button-primary-hover)",
      color: "var(--button-primary-foreground-hover)",
      border: "var(--button-primary-border-hover)",
    },
    "&.Mui-disabled": {
      backgroundColor: "var(--button-primary-disabled)",
      color: "var(--button-primary-disabled-foreground)",
      border: "var(--button-primary-disabled-border)",
    },
  },
  primaryOutlined: {
    backgroundColor: "var(--button-primary-outlined)",
    border: "var(--button-primary-outlined-border)",
    color: "var(--button-primary-outlined-foreground)",
    transition: "ease 0.2s",
    "&:hover": {
      backgroundColor: "var(--button-primary-outlined-hover)",
      border: "var(--button-primary-outlined-border-hover)",
      color: "var(--button-primary-outlined-foreground-hover)",
    },
    "&.Mui-disabled": {
      backgroundColor: "var(--button-primary-outlined-disabled)",
      border: "var(--button-primary-outlined-disabled-border)",
      color: "var(--button-primary-outlined-disabled-foreground)",
    },
  },
  secondary: {
    background: "var(--button-secondary)",
    color: "var(--button-secondary-foreground)",
    transition: "filter 0.3s ease",
    "&.Mui-disabled": {
      background: "var(--button-secondary-disabled)",
      color: "var(--button-secondary-disabled-foreground)",
    },
  },
  secondaryOutlined: {
    backgroundColor: "var(--button-secondary-outlined)",
    border: "var(--button-secondary-outlined-border)",
    color: "var(--button-secondary-outlined-foreground)",
    transition: "filter 0.3s ease",
    "&:hover": {
      backgroundColor: "var(--button-secondary-outlined-hover)",
      color: "var(--button-secondary-outlined-foreground-hover)",
      border: "var(--button-secondary-outlined-border-hover)",
    },
    "&.Mui-disabled": {
      backgroundColor: "var(--button-secondary-outlined-disabled)",
      color: "var(--button-secondary-outlined-disabled-foreground)",
      border: "var(--button-secondary-outlined-disabled-border)",
    },
  },
  danger: {
    transition: "ease 0.2s",
    backgroundColor: "var(--button-danger)",
    border: "var(--button-danger-border)",
    color: "var(--button-danger-foreground)",
    "&:hover": {
      backgroundColor: "var(--button-danger-hover)",
      color: "var(--button-danger-foreground-hover)",
      border: "var(--button-danger-border-hover)",
    },
    "&.Mui-disabled": {
      backgroundColor: "var(--button-danger-disabled)",
      color: "var(--button-danger-disabled-foreground)",
      border: "var(--button-danger-disabled-border)",
    },
  },
  dangerOutlined: {
    backgroundColor: "var(--button-danger-outlined)",
    border: "var(--button-danger-outlined-border)",
    color: "var(--button-danger-outlined-foreground)",
    transition: "ease 0.2s",
    "&:hover": {
      backgroundColor: "var(--button-danger-outlined-hover)",
      border: "var(--button-danger-outlined-border-hover)",
      color: "var(--button-danger-outlined-foreground-hover)",
    },
    "&.Mui-disabled": {
      backgroundColor: "var(--button-danger-outlined-disabled)",
      border: "var(--button-danger-outlined-disabled-border)",
      color: "var(--button-danger-outlined-disabled-foreground)",
    },
  },
  soft: {
    background: "var(--button-soft)",
    color: "var(--button-soft-foreground)",
    border: "var(--button-soft-border)",
    "&:hover": {
      border: "var(--button-soft-border-hover)",
      color: "var(--button-soft-foreground-hover)",
    },
    "&.Mui-disabled": {
      background: "var(--button-soft-disabled)",
      color: "var(--button-soft-disabled-foreground)",
      border: "var(--button-soft-disabled-border)",
    },
  },
  softOutlined: {
    backgroundColor: "var(--button-soft-outlined)",
    border: "var(--button-soft-outlined-border)",
    color: "var(--button-soft-outlined-foreground)",
    "&:hover": {
      backgroundColor: "var(--button-soft-outlined-hover)",
      border: "var(--button-soft-outlined-border-hover)",
      color: "var(--button-soft-outlined-foreground-hover)",
    },
    "&.Mui-disabled": {
      backgroundColor: "var(--button-soft-outlined-disabled)",
      color: "var(--button-soft-outlined-disabled-foreground)",
      border: "var(--button-soft-outlined-disabled-border)",
    },
  },
} as const;

export const Button: React.FC<CustomButtonProps> = ({
  children,
  variantType = ButtonVariantType.DEFAULT,
  fullWidth = false,
  sx,
  onClick = () => { },
  startIcon,
  permission = { module_name: "Default", permission: "Default" },
  ...muiProps
}) => {
  return (
    <RestrictedComponent permission={permission}>
      <MuiButton
        fullWidth={fullWidth}
        loadingPosition="center"
        startIcon={startIcon}
        sx={{
          textTransform: "none",
          borderRadius: "8px",
          fontWeight: 600,
          fontSize: "13px",
          fontFamily: "Segoe UI",
          minWidth: 0,
          gap: children ? "4px" : 0,
          padding: children ? "7px 16px" : "8px",
          "& .MuiButton-startIcon": {
            margin: 0,
          },
          ...variants[variantType],
          ...sx,
        }}
        onClick={onClick}
        {...muiProps}
      >
        {children && (
          <Box component="span" sx={{ pl: startIcon ? "6px" : "0px" }}>
            {children}
          </Box>
        )}
      </MuiButton>
    </RestrictedComponent>
  );
};
